/*
 * 소비도감 디자인 토큰 — 값의 SSOT
 * ------------------------------------------------------------------
 * docs/03-brand-design.md는 이 파일을 가리키고 값을 복제하지 않는다.
 * 대비 검산은 packages/tokens/contrast.test.ts가 기계로 재검증한다 (CHK-BR-03).
 *
 * 아트 앵커: 야간 표본실의 「도감」 — 어두운 작업대·산화 청록 잉크·표본 라벨·인주 스탬프.
 * 금지: 보라-파랑 그라데이션, 글래스모피즘, 핀테크 블루, 3D 클레이 아이콘.
 *
 * 테마: 웹 셸은 light/dark 모두 지원. 앱인토스 셸은 **light 고정**
 *       (docs/14-pinned-facts.md P-A2 — 미니앱 테마는 라이트 모드로 구현).
 */

:root {
	color-scheme: dark;

	/* ---------- 색: 표면 ---------- */
	--color-paper: #232929; /* 기본 배경 — 야간 표본실 작업대 */
	--color-surface: #2c3535; /* 카드·시트 */
	--color-surface-2: #18201f; /* 가라앉은 영역·미해금 도감 배경 */
	--color-surface-inverse: #e3edeb;

	/* ---------- 색: 텍스트 (대비는 vs --color-paper 기준) ---------- */
	--color-ink: #e3edeb; /* 12.37:1 AAA */
	--color-ink-muted: #bbc9c7; /* 8.65:1 AAA */
	--color-ink-subtle: #bdcac7; /* 6.58:1 AA — 최소 본문 등급 */
	--color-ink-inverse: #111716; /* 12.37:1 AAA (on --color-ink) */

	/* ---------- 색: 브랜드 ---------- */
	--color-primary: #63d0ca; /* 산화 청록 인장 — 5.00:1 (on #111716) */
	--color-primary-strong: #3fb6b5; /* 6.03:1 AA — 링크·강조 텍스트 */
	--color-primary-on: #111716; /* 5.00:1 AA — primary 위 텍스트 */
	--color-secondary: #6eb7ac; /* 표본 patina — 6.36:1 AA */
	--color-secondary-strong: #a7ddda; /* 10.19:1 AA */

	/* 희귀 해금 강조. --color-accent는 **장식 전용**(2.92:1) — 텍스트에 쓰지 않는다.
     텍스트가 필요하면 --color-accent-text를 쓴다. */
	--color-accent: #d7c45c;
	--color-accent-text: #d7c45c; /* 8.40:1 AA */
	--color-tag: #d7c45c;
	--color-tag-ink: #111716; /* 노랑 띠 위 작은 글자 AAA */

	/* ---------- 색: 시맨틱 ---------- */
	--color-success: #e3edeb; /* 6.36:1 AA */
	--color-danger: #f59b8d; /* 7.42:1 AA */
	--color-warning: #d7c45c; /* 8.40:1 AA */
	--color-info: #6eb7ac;

	/* ---------- 색: 경계 ---------- */
	--color-border: #4a5d5b; /* 장식 구분선 — WCAG 1.4.11 비대상 */
	--color-border-interactive: #78908d; /* 3.90:1 vs paper · 4.31:1 vs surface-2 — 입력·컨트롤 경계 */
	--color-focus: #6cdedb; /* 9.23:1 — 포커스 링 */

	/* ---------- 도감 상태 ---------- */
	--color-cell-locked: #18201f; /* 미해금 칸 배경 (경계는 --color-border-interactive) */
	--color-cell-stage1: #22302f; /* 1단계: 스탬프만 */
	--color-cell-stage2: #2c3535; /* 2단계: 실루엣 해제 */
	--color-cell-stage3: #2c3535; /* 3단계: 컬러 일러스트 + --color-accent 테두리 */

	/* ---------- 타이포 ---------- */
	/* 두 채널 공용 system-only 전략. 로드하지 않는 제품명 폰트를 선언하지 않는다. */
	--font-sans:
		system-ui, -apple-system, BlinkMacSystemFont, "Apple SD Gothic Neo",
		"Malgun Gothic", sans-serif;
	--font-serif:
		ui-serif, "AppleMyungjo", "Batang", serif; /* 도감 라벨·연말 결산 전용 */
	--font-mono:
		ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace; /* 금액 정렬 */

	--text-2xs: 0.6875rem; /* 11px — 표본 라벨 */
	--text-xs: 0.75rem; /* 12px */
	--text-sm: 0.875rem; /* 14px */
	--text-base: 1rem; /* 16px — 본문 최소 */
	--text-lg: 1.125rem; /* 18px */
	--text-xl: 1.375rem; /* 22px */
	--text-2xl: 1.75rem; /* 28px */
	--text-3xl: 2.25rem; /* 36px — 해금 연출·결산 표지 */

	--leading-tight: 1.25;
	--leading-normal: 1.55;
	--leading-relaxed: 1.75;
	--weight-regular: 400;
	--weight-medium: 500;
	--weight-bold: 700;

	/* ---------- 공간 ---------- */
	--space-1: 0.25rem;
	--space-2: 0.5rem;
	--space-3: 0.75rem;
	--space-4: 1rem;
	--space-5: 1.5rem;
	--space-6: 2rem;
	--space-7: 3rem;
	--space-8: 4rem;

	/* ---------- 모서리·고도 ---------- */
	--radius-sm: 4px;
	--radius-md: 8px;
	--radius-lg: 14px;
	--radius-full: 999px;

	--elevation-1: 0 1px 2px rgb(8 16 15 / 0.22);
	--elevation-2: 0 2px 8px rgb(8 16 15 / 0.3);
	--elevation-3: 0 8px 24px rgb(8 16 15 / 0.42);

	/* ---------- 모션 ---------- */
	--duration-instant: 90ms;
	--duration-fast: 180ms;
	--duration-normal: 260ms;
	--duration-reveal: 1500ms; /* 도감 해금 연출 총 길이 */
	--ease-standard: cubic-bezier(0.2, 0, 0, 1);
	--ease-enter: cubic-bezier(0, 0, 0, 1);
	--ease-stamp: cubic-bezier(
		0.34,
		1.56,
		0.64,
		1
	); /* 스탬프가 찍히는 오버슈트 */

	/* ---------- 터치 ---------- */
	--touch-min: 44px;
	--tap-gap-min: 8px;
}

@media (prefers-color-scheme: dark) {
	:root:not([data-theme="light"]) {
		color-scheme: dark;

		--color-paper: #151d1c;
		--color-surface: #1f2a29;
		--color-surface-2: #101817;
		--color-surface-inverse: #e3edeb;

		--color-ink: #e3edeb;
		--color-ink-muted: #bbc9c7;
		--color-ink-subtle: #bdcac7;
		--color-ink-inverse: #101817;

		--color-primary: #63d0ca;
		--color-primary-strong: #6cdedb;
		--color-primary-on: #101817;
		--color-secondary: #6eb7ac;
		--color-secondary-strong: #a7ddda;

		--color-accent: #d7c45c;
		--color-accent-text: #d7c45c;
		--color-tag: #d7c45c;
		--color-tag-ink: #101817;

		--color-success: #e3edeb;
		--color-danger: #f59b8d;
		--color-warning: #d7c45c;
		--color-info: #6eb7ac;

		--color-border: #3f5754;
		--color-border-interactive: #78908d;
		--color-focus: #6cdedb;

		--color-cell-locked: #101817;
		--color-cell-stage1: #1b2927;
		--color-cell-stage2: #1f2a29;
		--color-cell-stage3: #1f2a29;
	}
}

/*
 * light — 2026-10 완성 웨이브 시각 언어: 결제 뒤에 남는 실제 물건.
 * 감열지 영수증(차가운 회백 바탕) · 영수증 인쇄 잉크(청흑) · 마트 가격표 노랑(띠 전용)
 * · 산화 청록 인장(주 버튼). 크림 바탕·세리프·테라코타는 쓰지 않는다.
 * 앱인토스는 공개 정책(P-A2)에 따라 이 light 토큰을 강제한다.
 * 대비는 contrast.test.ts가 이 블록을 읽어 재계산한다.
 */
:root[data-theme="light"] {
	color-scheme: light;
	--color-paper: #f2f4f3; /* 감열지 */
	--color-surface: #ffffff;
	--color-surface-2: #e5eae8;
	--color-surface-inverse: #1e2328;
	--color-ink: #1e2328; /* 영수증 잉크 14.33:1 */
	--color-ink-muted: #4a535b; /* 7.09:1 */
	--color-ink-subtle: #505b62; /* 5.01:1 */
	--color-ink-inverse: #f2f4f3;
	--color-primary: #007c7a; /* 산화 청록 — 흰 글자 5.04:1 */
	--color-primary-strong: #00605e; /* 링크 6.71:1 */
	--color-primary-on: #ffffff;
	--color-secondary: #1e2328;
	--color-secondary-strong: #1e2328;
	--color-accent: #ffd23f; /* 가격표 노랑 — 배경 띠 전용, 글자 색 금지 */
	--color-accent-text: #8a5a00;
	--color-tag: #ffd23f;
	--color-tag-ink: #1e2328; /* 노랑 띠 위 글자 10.97:1 */
	--color-success: #1e2328;
	--color-danger: #b42318;
	--color-warning: #8a5a00;
	--color-info: #1e2328;
	--color-border: #d3dad7;
	--color-border-interactive: #7d8a86; /* 3.25:1 */
	--color-focus: #1e2328;
	--color-cell-locked: #e1e6e4;
	--color-cell-stage1: #eef2f0;
	--color-cell-stage2: #ffffff;
	--color-cell-stage3: #ffffff;
	--font-serif: var(--font-sans);
	--elevation-1: 0 1px 2px rgb(30 35 40 / 0.06);
	--elevation-2: 0 2px 8px rgb(30 35 40 / 0.08);
	--elevation-3: 0 8px 24px rgb(30 35 40 / 0.12);
}

/* 웹의 명시적 다크 선택은 OS 설정보다 우선한다. */
:root[data-theme="dark"] {
	color-scheme: dark;
}

@media (prefers-reduced-motion: reduce) {
	:root {
		--duration-instant: 1ms;
		--duration-fast: 1ms;
		--duration-normal: 1ms;
		--duration-reveal: 1ms;
	}
}
